import { VisitApprovalRowProgressState, VisitApprovalRowProgressStatus } from '@jms/model';
import { StatusTone } from '../shared/status.tag.tones';

/** One live state's badge. `busy` drives the spinner; only the two transient states set it. */
export interface VisitApprovalProgressBadge {
  label: string;
  tone: StatusTone;
  busy: boolean;
  detail?: string;
}

const PROGRESS_TONES: Record<VisitApprovalRowProgressStatus, StatusTone> = {
  Queued: 'neutral',
  Running: 'brand',
  Approved: 'success',
  Reapproved: 'success',
  AlreadyApproved: 'neutral',
  Withdrawn: 'neutral',
  NeedsReview: 'warning',
  Failed: 'error',
  NotProcessed: 'neutral',
};

const PROGRESS_LABELS: Record<VisitApprovalRowProgressStatus, string> = {
  Queued: 'Queued',
  Running: 'Approving',
  Approved: 'Approved',
  Reapproved: 'Reapproved',
  AlreadyApproved: 'Already approved',
  Withdrawn: 'Withdrawn',
  NeedsReview: 'Needs review',
  Failed: 'Failed',
  NotProcessed: 'Not processed',
};

/**
 * The tooltip text, which is the only place a reason or an owning approval is readable.
 *
 * A badge is four words at most, so a row that needs review or failed would otherwise tell the planner
 * nothing about WHY — which is the whole point of surfacing the state per row rather than as a count.
 */
function progressDetail(state: VisitApprovalRowProgressState): string | undefined {
  switch (state.status) {
    case 'NeedsReview':
      return state.owner
        ? `${state.reason} · Owned by approval ${state.owner.approvalId}`
        : state.reason;
    case 'Failed':
      return `${state.message} (${state.code})`;
    case 'NotProcessed':
      return state.reason;
    default:
      return undefined;
  }
}

export function visitApprovalProgressBadge(state: VisitApprovalRowProgressState): VisitApprovalProgressBadge {
  return {
    label: PROGRESS_LABELS[state.status],
    tone: PROGRESS_TONES[state.status],
    busy: state.status === 'Queued' || state.status === 'Running',
    detail: progressDetail(state),
  };
}
